import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminPage, Pill } from "@/pages/admin/AdminLayout";

export default function ContactAdmin() {
  const [msgs, setMsgs] = useState([]);

  const load = () => api.get("/admin/contact-messages").then((r) => setMsgs(r.data));

  useEffect(() => {
    load();
  }, []);

  const toggle = async (m) => {
    await api.patch(`/admin/contact-messages/${m.id}`, null, { params: { handled: !m.handled } });
    load();
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete the message from ${m.name} (${m.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/contact-messages/${m.id}`);
      setMsgs((list) => list.filter((x) => x.id !== m.id));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete message");
    }
  };

  return (
    <AdminPage
      title="Contact messages"
      lede={`${msgs.length} message${msgs.length === 1 ? "" : "s"} received through the contact form. Each submission is stored here and emailed to the routed mailbox — anything marked "email failed" was saved but not delivered, so reply manually from the address on the message.`}
    >
      <div className="space-y-4" data-testid="admin-contact-list">
        {msgs.length === 0 && (
          <div className="bg-white border border-line px-6 py-8 text-[13.5px] font-light text-[#6E6A63]">
            No messages yet.
          </div>
        )}
        {msgs.map((m) => (
          <div
            key={m.id}
            data-testid={`contact-message-${m.id}`}
            className="bg-white border border-line px-6 py-6"
          >
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-[15px] text-charcoal">{m.name}</span>
                <a
                  href={`mailto:${m.email}`}
                  className="text-[13.5px] font-light text-sage-dark link-underline"
                  data-testid={`contact-reply-${m.id}`}
                >
                  {m.email}
                </a>
                <Pill tone="sage">{m.topic}</Pill>
                <Pill tone={m.handled ? "green" : "yellow"}>{m.handled ? "handled" : "new"}</Pill>
                {m.delivery?.status && (
                  <span data-testid={`contact-delivery-${m.id}`}>
                    {m.delivery.status === "sent" ? (
                      <Pill tone="green">emailed {m.delivery.recipient || ""}</Pill>
                    ) : m.delivery.status === "failed" ? (
                      <Pill tone="red">email failed</Pill>
                    ) : (
                      <Pill tone="yellow">email {m.delivery.status}</Pill>
                    )}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-4">
                <span className="text-[12px] font-light text-[#6E6A63]">
                  {String(m.created_at || "").slice(0, 16).replace("T", " ")}
                </span>
                <button
                  onClick={() => toggle(m)}
                  data-testid={`contact-toggle-${m.id}`}
                  className="text-[11px] tracking-[0.12em] uppercase text-charcoal border border-line px-3 py-1.5 hover:bg-bone transition-colors"
                >
                  {m.handled ? "Mark new" : "Mark handled"}
                </button>
                <button
                  onClick={() => remove(m)}
                  data-testid={`contact-delete-${m.id}`}
                  className="text-[11px] tracking-[0.12em] uppercase text-[#991B1B]/70 hover:text-[#991B1B] border border-line px-3 py-1.5 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
            <p className="text-[14.5px] font-light leading-[1.7] text-[#4A4A4A] mt-4 whitespace-pre-wrap">
              {m.message}
            </p>
          </div>
        ))}
      </div>
    </AdminPage>
  );
}
